import React from 'react';
import { Trash2, Info } from 'lucide-react';
import ColorSwatchPicker from './ColorSwatchPicker';

interface DividerBlockData {
  style: 'solid' | 'dashed' | 'dotted' | 'double';
  color: string;
  thickness: number;
  spacing: number;
}

interface DividerBlockProps {
  data: DividerBlockData;
  onChange: (data: DividerBlockData) => void;
  onDelete: () => void;
  isPreview?: boolean;
}

const DividerBlock: React.FC<DividerBlockProps> = ({ data, onChange, onDelete, isPreview }) => {
  if (isPreview) {
    return (
      <div style={{ marginTop: `${data.spacing}px`, marginBottom: `${data.spacing}px` }}>
        <hr
          style={{
            border: 'none',
            borderTop: `${data.thickness}px ${data.style} ${data.color}`,
            margin: 0
          }}
        />
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">เส้นแบ่ง</h3>
          <div className="relative">
            <Info className="w-4 h-4 text-gray-400 cursor-help peer" />
            <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
              <p className="font-semibold mb-1">แบ่งส่วนเนื้อหา</p>
              <p className="text-gray-300">เพิ่มเส้นแบ่งเพื่อแยกส่วนเนื้อหาต่างๆ ให้ดูเป็นระเบียบและอ่านง่ายขึ้น</p>
            </div>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">รูปแบบเส้น</label>
          <div className="flex items-center gap-2">
            <ColorSwatchPicker
              compact
              color={data.color}
              onChange={(hex) => onChange({ ...data, color: hex })}
            />
            <select
              value={data.style}
              onChange={(e) => onChange({ ...data, style: e.target.value as any })}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="solid">เส้นทึบ</option>
              <option value="dashed">เส้นประ</option>
              <option value="dotted">เส้นจุด</option>
              <option value="double">เส้นคู่</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">ความหนา: {data.thickness}px</label>
          <input
            type="range"
            min="1"
            max="10"
            value={data.thickness}
            onChange={(e) => onChange({ ...data, thickness: parseInt(e.target.value) })}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">ระยะห่าง: {data.spacing}px</label>
          <input
            type="range"
            min="0"
            max="100"
            value={data.spacing}
            onChange={(e) => onChange({ ...data, spacing: parseInt(e.target.value) })}
            className="w-full"
          />
        </div>

        <div className="pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-2">ตัวอย่าง:</p>
          <div style={{ marginTop: `${data.spacing / 2}px`, marginBottom: `${data.spacing / 2}px` }}>
            <hr
              style={{
                border: 'none',
                borderTop: `${data.thickness}px ${data.style} ${data.color}`,
                margin: 0
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DividerBlock;
